
import { useState, useEffect } from 'react';
import { Vendor } from '@/types';
import { useToast } from '@/hooks/use-toast';
import VendorSelect from '@/components/VendorSelect';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ProcessingHistoryProps {
  selectedVendor: Vendor | null;
  onVendorSelect: (vendor: Vendor | null) => void;
}

interface ProcessingRecord {
  id: string;
  vendorName: string;
  status: string;
  ProductFeedUpdated: boolean;
  FinalPriceCalculated: boolean;
  ShopifyTriggered: boolean;
  FreighClubReportGenerated: boolean;
  FreighClubReportURL?: string;
  ReportGenerated: boolean;
  invalidArchiveKey?: string;
}

interface ProcessingHistoryResponse {
  data: ProcessingRecord[];
}

const ProcessingHistory = ({ selectedVendor, onVendorSelect }: ProcessingHistoryProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [history, setHistory] = useState<ProcessingRecord[]>([]);
  const { toast } = useToast();

  const fetchHistory = async () => {
    setLoading(true);
    
    // Build the URL based on whether a vendor is selected or not
    let url = 'https://nestingale.com/v1/api/fileProcessStatus?opType=ProductPrice';
    
    if (selectedVendor) {
      url = `https://nestingale.com/v1/api/fileProcessStatus?vendor=${encodeURIComponent(selectedVendor.vendorName)}&opType=ProductPrice`;
      console.log(`Fetching processing history for specific vendor: ${selectedVendor.vendorName}`);
    } else {
      console.log('Fetching processing history for all vendors');
    }
    
    console.log('Request URL:', url);

    try {
      const response = await fetch(url);
      console.log('Processing history API response status:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Processing history API error response:', errorText);
        throw new Error(`Failed to fetch history: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const responseData: ProcessingHistoryResponse = await response.json();
      console.log('Processing history API response:', responseData);
      
      // Check if data property exists and is an array
      if (responseData && Array.isArray(responseData.data)) {
        console.log(`Retrieved ${responseData.data.length} processing records`);
        setHistory(responseData.data);
      } else {
        console.warn('Processing history data is missing or not an array:', responseData);
        setHistory([]);
      }
    } catch (error) {
      console.error('Error fetching processing history:', error);
      toast({
        title: "Error",
        description: `Failed to load processing history: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive"
      });
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('ProcessingHistory component - useEffect triggered');
    fetchHistory();
  }, [selectedVendor]);

  const handleDownload = (url: string | undefined, fileName: string) => {
    if (!url) {
      console.error('Download URL is not available');
      toast({
        title: "Error",
        description: "Download URL is not available",
        variant: "destructive"
      });
      return;
    }
    
    console.log(`Downloading file from URL: ${url}`);
    console.log(`File will be saved as: ${fileName}`);
    
    // Create a temporary anchor element
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'download.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log('Download initiated');
  };

  // Function to render boolean values as checkmarks or x marks
  const renderBoolean = (value: boolean) => {
    return value ? 
      <span className="text-green-600 font-bold"><Check size={16} /></span> : 
      <span className="text-red-600 font-bold"><X size={16} /></span>;
  };

  // Function to render FreightClub column with download button if URL exists
  const renderFreightClubColumn = (record: ProcessingRecord) => {
    return (
      <div className="flex items-center gap-2 justify-center">
        {renderBoolean(record.FreighClubReportGenerated)}
        {record.FreighClubReportURL && (
          <Button variant="ghost" size="sm" onClick={() => handleDownload(record.FreighClubReportURL, `freightclub-${record.id}.xlsx`)}>
            <Download size={16} className="text-blue-600" />
          </Button>
        )}
      </div>
    );
  };

  // Function to render Report Generated column with download button
  const renderReportGeneratedColumn = (record: ProcessingRecord) => {
    return (
      <div className="flex items-center gap-2 justify-center">
        {renderBoolean(record.ReportGenerated)}
        {record.invalidArchiveKey && (
          <Button variant="ghost" size="sm" onClick={() => handleDownload(record.invalidArchiveKey, `report-${record.id}.csv`)}>
            <Download size={16} className="text-blue-600" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4 w-full">
      <Card className="w-full max-w-none">
        <CardHeader>
          <CardTitle>Processing History</CardTitle>
          <CardDescription>
            {selectedVendor 
              ? `View processing history for ${selectedVendor.displayName}`
              : 'View processing history for all vendors'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <VendorSelect onVendorSelect={onVendorSelect} disabled={loading} />
          
          {loading ? (
            <div className="py-6 text-center text-gray-500">Loading history data...</div>
          ) : history.length === 0 ? (
            <div className="py-6 text-center text-gray-500">
              {selectedVendor 
                ? `No processing records found for ${selectedVendor.displayName}` 
                : "No processing records found"}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Product Feed Updated</TableHead>
                    <TableHead>Price Calculated</TableHead>
                    <TableHead>Shopify Updated</TableHead>
                    <TableHead>Freight Club Updated</TableHead>
                    <TableHead>Report Generated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>{record.id}</TableCell>
                      <TableCell>{record.vendorName}</TableCell>
                      <TableCell>{record.status}</TableCell>
                      <TableCell className="text-center">{renderBoolean(record.ProductFeedUpdated)}</TableCell>
                      <TableCell className="text-center">{renderBoolean(record.FinalPriceCalculated)}</TableCell>
                      <TableCell className="text-center">{renderBoolean(record.ShopifyTriggered)}</TableCell>
                      <TableCell className="text-center">{renderFreightClubColumn(record)}</TableCell>
                      <TableCell className="text-center">{renderReportGeneratedColumn(record)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProcessingHistory;
